import { useState, useCallback } from "react";
import { RedeemCreditDialog } from "@/components/RedeemCreditDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MailOpen, Loader2, User, CalendarDays, ListOrdered, CalendarX2, Award, Ticket } from "lucide-react";
import { fDate, fTime } from "@/components/TicketSlide";

const CREDIT_STATE = { available: "Disponible", reserved: "Invitación pendiente", redeeming: "Canjeando", used: "Canjeada" };

function InviteRow({ inv, onDone }) {
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/invites/${inv.id}/${kind}`);
      toast.success(kind === "accept" ? "¡Listo! Tu ticket de canje ya está en Mis Tickets." : "Invitación rechazada. Conservas tu crédito.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4" data-testid={`invite-${inv.id}`}>
      <p className="text-sm"><strong>{inv.business_name}</strong> te invita a una sesión de <strong>{inv.workshop_name}</strong></p>
      <p className="text-xs text-muted-foreground mt-0.5">{fDate(inv.session_at)} · {fTime(inv.session_at)} hrs · usa 1 crédito</p>
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={() => act("accept")} disabled={!!busy} data-testid={`invite-accept-${inv.id}`}>
          {busy === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aceptar y canjear"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => act("decline")} disabled={!!busy} data-testid={`invite-decline-${inv.id}`}>Rechazar</Button>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="text-sm font-medium break-words" data-testid={testId}>{children}</div>
    </div>
  </div>
);

function PrizeCard({ c, onRedeem }) {
  const available = c.status === "available";
  return (
    <div className={`rounded-3xl overflow-hidden border bg-card shadow-[0_14px_40px_-16px_rgba(121,68,228,0.45)] ${available ? "" : "opacity-75"}`} data-testid={`prize-${c.id}`}>
      <div className="bg-primary text-primary-foreground p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">VIVOTaller · Mi crédito</p>
          <Badge className={available ? "bg-sun text-[#3d2b00] hover:bg-sun" : "bg-white/20 hover:bg-white/20"} data-testid={`prize-state-${c.id}`}>{CREDIT_STATE[c.status]}</Badge>
        </div>
        <h3 className="font-display font-extrabold text-xl mt-2 leading-tight break-words" data-testid={`prize-name-${c.id}`}>{c.source_workshop_name}</h3>
        <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Maestro: <strong>{c.business_name || "—"}</strong></p>
      </div>
      <div className="relative flex items-center">
        <div className="w-5 h-5 rounded-full bg-background -ml-2.5 border-r" />
        <div className="flex-1 border-t-2 border-dashed border-border" />
        <div className="w-5 h-5 rounded-full bg-background -mr-2.5 border-l" />
      </div>
      <div className="p-5 space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <Field icon={CalendarDays} label="Fecha de la sesión" testId={`prize-session-date-${c.id}`}>{fDate(c.source_session_at)}</Field>
          <Field icon={ListOrdered} label="Número de sesión" testId={`prize-session-number-${c.id}`}>
            {c.session_number ? `Sesión ${c.session_number}${c.sessions_total ? ` de ${c.sessions_total}` : ""}` : "—"}
          </Field>
        </div>
        <Field icon={CalendarX2} label="Cancelaste el" testId={`prize-cancelled-${c.id}`}>
          {c.cancelled_at ? `${fDate(c.cancelled_at)} · ${fTime(c.cancelled_at)} hrs` : "—"}
        </Field>
        {c.status === "used" && c.used_workshop_name && (
          <p className="text-xs text-muted-foreground" data-testid={`prize-used-${c.id}`}>Canjeada en {c.used_workshop_name}{c.used_session_at ? ` · ${fDate(c.used_session_at)} ${fTime(c.used_session_at)} hrs` : ""}</p>
        )}
        {available && (
          <Button className="w-full gap-1.5" onClick={() => onRedeem(c)} data-testid={`prize-redeem-${c.id}`}><Ticket className="w-4 h-4" />Canjear por una sesión</Button>
        )}
      </div>
    </div>
  );
}

export function SessionCredits({ data, onChanged }) {
  const [redeem, setRedeem] = useState(null);
  const close = useCallback(() => setRedeem(null), []);
  if (!data) return <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mt-16" />;
  return (
    <div data-testid="session-credits">
      {redeem && <RedeemCreditDialog credit={redeem} onClose={close} onDone={() => { setRedeem(null); onChanged(); }} />}
      <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm flex gap-3" data-testid="prizes-info">
        <Award className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p>Recibes un crédito cuando avisas a tiempo que no asistirás a una sesión. Canjéalo tú mismo por una sesión con cupos del mismo taller u otro del mismo Maestro, o acepta una invitación del Maestro. Cada crédito se usa una sola vez.</p>
      </div>
      {data.invites.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="invites-list">
          <p className="text-sm font-semibold flex items-center gap-1.5"><MailOpen className="w-4 h-4 text-primary" />Invitaciones recibidas</p>
          {data.invites.map((i) => <InviteRow key={i.id} inv={i} onDone={onChanged} />)}
        </div>
      )}
      {data.credits.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="prizes-empty">
          <Award className="w-10 h-10 mx-auto mb-3 opacity-40" />Aún no tienes créditos.
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-5" data-testid="prizes-list">
          {data.credits.map((c) => <PrizeCard key={c.id} c={c} onRedeem={setRedeem} />)}
        </div>
      )}
    </div>
  );
}
